// @flow
import invariant from 'invariant';
import algoliasearch from 'algoliasearch';
import queryString from 'query-string';
import trackError from 'utils/trackError';
import RichError from 'utils/RichError';
import { postJson } from 'utils/http';
import { configValue } from 'utils/config';

class AlgoliaValidUntilError {}
class AlgoliaGeneralError extends Error {}

const isValidUntilError = error =>
  error.message && error.message.includes('validUntil');

export default (
  {
    appId,
    apiKey
  }: $ReadOnly<{
    appId: string,
    apiKey: string
  }>,
  query: string,
  queryOptions: QueryOptions,
  analytics: boolean = true,
  updateApiKey?: Function
) => {
  invariant(appId, 'Search app id must be configured');
  invariant(apiKey, 'Search api key must be configured');

  const { index, ...options } = queryOptions;

  const params = {
    getRankingInfo: true,
    clickAnalytics: true,
    analytics,
    ...options,
    query: query ? query.trim() : ''
  };

  let client = algoliasearch(appId, apiKey);
  let indexClient = client.initIndex(index);
  let request = indexClient.search(params);
  return request.catch(e => {
    if (isValidUntilError(e)) {
      if (updateApiKey) {
        return updateApiKey().then(newApiKey => {
          client = algoliasearch(appId, newApiKey);
          indexClient = client.initIndex(index);
          request = indexClient.search(params);
          return request;
        });
      }

      throw new AlgoliaValidUntilError();
    }
    const requestInfo = {
      debugData: e.debugData,
      errorMessage: e.message,
      requestParams: queryString.stringify(params),
      statusCode: e.statusCode,
      response: e.response
    };
    trackError(
      new RichError('Algolia POST request error.', {
        requestInfo: JSON.stringify(requestInfo)
      })
    );
    throw new AlgoliaGeneralError(
      `Algolia Error: ${JSON.stringify(requestInfo)}`
    );
  });
};

export const getObject = (
  {
    appId,
    apiKey
  }: $ReadOnly<{
    appId: string,
    apiKey: string
  }>,
  index: string,
  id: string,
  attributes: Array<string>
) => {
  invariant(appId, 'Search app id must be configured');
  invariant(apiKey, 'Search api key must be configured');

  const client = algoliasearch(appId, apiKey);
  const indexClient = client.initIndex(index);
  const request = indexClient.getObject(id, attributes);
  return request.catch(e => {
    if (isValidUntilError(e)) {
      throw new AlgoliaValidUntilError();
    }
    trackError(
      new RichError('Algolia POST request error.', {
        debugData: e.debugData,
        errorMessage: e.message,
        requestParams: queryString.stringify({ id, attributes }),
        statusCode: e.statusCode,
        response: e.response
      })
    );
  });
};

export const getObjects = (
  {
    appId,
    apiKey
  }: $ReadOnly<{
    appId: string,
    apiKey: string
  }>,
  index: string,
  ids: Array<string>,
  attributes: Array<string>
): ?Array<Object> => {
  invariant(appId, 'Search app id must be configured');
  invariant(apiKey, 'Search api key must be configured');

  const client = algoliasearch(appId, apiKey);
  const indexClient = client.initIndex(index);
  const request = indexClient.getObjects(ids, attributes);
  return request
    .then(({ results }) => results)
    .catch(e => {
      if (isValidUntilError(e)) {
        throw new AlgoliaValidUntilError();
      }
      trackError(
        new RichError('Algolia POST request error.', {
          debugData: JSON.stringify(e.debugData),
          errorMessage: e.message,
          requestParams: queryString.stringify({ ids, attributes }),
          statusCode: e.statusCode,
          response: e.response
        })
      );
      return [];
    });
};

// Algolia analytics tracking. Note that position is 1-indexed
// This function is a no-op if ALGOLIA_ANALYTICS_API_KEY is not set
// i.e., sandboxes algolia environments like dev
export const algoliaTrackClick = (
  queryID: string,
  objectID: string,
  position: number
) => {
  invariant(queryID, 'No query id supplied');
  invariant(objectID, 'No object id supplied');
  invariant(position, 'Invalid position supplied');
  const ALGOLIA_CLICKS_URL = 'https://insights.algolia.io/1/searches/click';
  const ANALYTICS_KEY = configValue('algolia', 'analyticsApiKey');
  const headers = {
    'X-Algolia-API-Key': ANALYTICS_KEY,
    'X-Algolia-Application-Id': configValue('algolia', 'appId')
  };
  const data = { queryID, objectID, position };
  return ANALYTICS_KEY && postJson(ALGOLIA_CLICKS_URL, data, { headers });
};
